import React, { useContext, useEffect, useState, useMemo } from 'react'; 
import { useNavigate } from 'react-router-dom';
import styles from './styles/ClassroomAssignments.module.css';
import { ClassroomContext } from '../../context/ClassroomContext';
import ClassroomAssignmentsModal from './ClassroomAssignmentsModal';
import ClassroomAssignmentsFilter from './ClassroomAssignmentsFilter';
import AuthContext from '../../context/AuthContext';
import { FaPlus, FaFilter, FaTimesCircle, FaEdit, FaTrashAlt, FaEye, FaLink, FaUnlink, FaUserCircle } from 'react-icons/fa';
import Loading from '../loading/Loading';

const ClassroomAssignments = ({ fromDashboard }) => {
    const {
        classroom,
        classroomAssignments,
        isTeacher,
        deleteAssignment,
        fetchClassroomAssignments,
        isFetchingAssignments,
    } = useContext(ClassroomContext);

    const { userId } = useContext(AuthContext);
    const [showModal, setShowModal] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [filter, setFilter] = useState(null);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const baseAssignments = useMemo(() => {
        if (!classroomAssignments) return [];
        
        let processed = [...classroomAssignments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        if (!isTeacher) {
            processed = processed.filter(assignment =>
                assignment.students.some(student => student._id === userId)
            );
        }

        if (filter) {
            processed = processed.filter((assignment) => {
                const dateValue = assignment[filter.dateType];
                if (!dateValue) return false;
                const assignmentDate = new Date(dateValue);
                const assignmentLocal = new Date(assignmentDate.getFullYear(), assignmentDate.getMonth(), assignmentDate.getDate());
                const [startYear, startMonth, startDay] = filter.startDate.split('-').map(Number);
                const startLocal = new Date(startYear, startMonth - 1, startDay);
                const [endYear, endMonth, endDay] = filter.endDate.split('-').map(Number);
                const endLocal = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
                return assignmentLocal >= startLocal && assignmentLocal <= endLocal;
            });
        }
        return processed;
    }, [classroomAssignments, isTeacher, userId, filter]);

    
    
    
    
    
    

    const openCreateModal = () => {
        setEditingAssignment(null);
        setShowModal(true);
    };

    const openEditModal = (assignment) => {
        setEditingAssignment(assignment);
        setShowModal(true);
    };

    const handleDelete = async (assignmentId) => {
        if (window.confirm('Are you sure you want to delete this assignment?')) {
            await deleteAssignment(assignmentId);
        }
    };

    const handleViewSubmissions = (assignment) => {
        navigate(`/my-classrooms/${classroom._id}/assignments/${assignment._id}`);
    };

    const openFilterModal = () => {
        setShowFilterModal(true);
    };

    const closeFilterModal = () => {
        setShowFilterModal(false);
    };

    const applyFilter = (newFilter) => {
        setFilter(newFilter);
        closeFilterModal();
    };

    const clearFilter = () => {
        setFilter(null);
    };

    
    
    
    
    
    
    

    return (
        <div className={styles.container}>
            {/* --- Header Section --- */}
            {!fromDashboard &&(
                <div className={styles.header}>
                    <div className={styles.headerActionsLeft}>
                        {isTeacher && (
                            <button className={`${styles.button} ${styles.buttonPrimary}`} onClick={openCreateModal} title="Create New Assignment">
                                <FaPlus /> Create Assignment
                            </button>
                        )}
                    </div>
                    <div className={styles.headerActionsRight}>
                        <button className={`${styles.button} ${styles.buttonSecondary}`} onClick={openFilterModal} title={filter ? 'Edit current filter' : 'Filter assignments'}>
                            <FaFilter /> {filter ? 'Edit Filter' : 'Filter'}
                        </button>
                        {filter && (
                            <>
                                <div className={styles.filterDetails}>
                                    <span className={styles.filterLabel}>Active Filter:</span>
                                    <span>{filter.dateType === 'createdAt' ? 'Assigned' : 'Due'}</span>
                                    <span>{filter.startDate} → {filter.endDate}</span>
                                </div>
                                <button className={`${styles.button} ${styles.buttonIcon} ${styles.clearFilterButton}`} onClick={clearFilter} title="Clear current filter">
                                    <FaTimesCircle />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
    
            {/* --- Assignments List --- */}
            <div className={styles.assignmentsList}>
                {baseAssignments.length === 0 ? (
                    <div className={styles.noAssignmentsMessage}>
                        <p>No assignments found{filter ? ' matching your filter' : ''}.</p>
                        {!filter && isTeacher && <p>Click "Create Assignment" to get started.</p>}
                        {filter && <button className={`${styles.button} ${styles.buttonText}`} onClick={clearFilter}>Clear Filter?</button>}
                    </div>
                ) : (
                    baseAssignments.map((assignment) => (
                        <div key={assignment._id} className={styles.assignmentCard}>
                            {/* Card Header (Dates) */}
                            <div className={styles.assignmentHeader}>
                                <span className={styles.assignedDate}>
                                    Assigned: {new Date(assignment.createdAt).toLocaleDateString()}
                                </span>
                                {assignment.dueDate && (
                                    <span className={styles.dueDate}>
                                        Due: {new Date(assignment.dueDate).toLocaleDateString()}
                                    </span>
                                )}
                            </div>
    
                            {/* Main Content */}
                            <div className={styles.assignmentBody}>
                                <h3 className={styles.assignmentTitle}>{assignment.title}</h3>
                                {assignment.description && (
                                    <p className={styles.assignmentDescription}>{assignment.description}</p>
                                )}
    
                                {/* Scenario Link */}
                                <div className={styles.scenarioContainer}>
                                    {assignment.scenario ? (
                                        assignment.scenario.isPublished ? (
                                            <button
                                                className={`${styles.button} ${styles.buttonText} ${styles.scenarioLink}`}
                                                onClick={() => navigate(`/view/${assignment.scenario._id}`)}
                                                title={`View Scenario: ${assignment.scenario.title || 'Untitled'}`}
                                            >
                                                <FaLink /> {assignment.scenario.title || "Untitled Scenario"}
                                            </button>
                                        ) : (
                                            <span
                                                className={styles.scenarioTitleUnpublished}
                                                title="This scenario is not published and cannot be viewed by students."
                                            >
                                               <FaUnlink /> {assignment.scenario.title || "Untitled Scenario"} (Unpublished)
                                            </span>
                                        )
                                    ) : (
                                        <span className={styles.scenarioTitleMissing}>
                                            <FaUnlink /> Scenario not linked
                                        </span>
                                    )}
                                </div>
    
                                {/* Students List */}
                                 {(Array.isArray(assignment.students) && assignment.students.length > 0) || (isTeacher && (!Array.isArray(assignment.students) || assignment.students.length === 0)) ? (
                                    <div className={styles.studentsSection}>
                                        <h4 className={styles.studentsHeader}>Assigned Students:</h4>
                                        <div className={styles.studentsContainer}>
                                            {Array.isArray(assignment.students) && assignment.students.length > 0 ? (
                                                assignment.students.map((student) => (
                                                    <div key={student._id || student.id} className={styles.student}>
                                                        {student.profilePicture ? (
                                                            <img
                                                                src={student.profilePicture}
                                                                alt={`${student.username || 'Student'}`}
                                                                className={styles.studentAvatar}
                                                            />
                                                        ) : (
                                                             <div className={styles.studentAvatarPlaceholder}>
                                                                <FaUserCircle /> {/* Placeholder Icon */}
                                                             </div>
                                                        )}
                                                        <span className={styles.studentName}>{student.username || 'Unnamed Student'}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                 isTeacher && <p className={styles.noStudentsMessage}>No students assigned yet.</p>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
    
    
                                {/* Teacher Actions */}
                                {(isTeacher && !fromDashboard) && (
                                    <div className={styles.teacherActions}>
                                        <button
                                            className={`${styles.button} ${styles.buttonSecondary}`}
                                            onClick={() => openEditModal(assignment)}
                                        >
                                            <FaEdit /> Edit
                                        </button>
                                         <button
                                            className={`${styles.button} ${styles.buttonSecondary}`}
                                            onClick={() => handleViewSubmissions(assignment)}
                                        >
                                            <FaEye /> Submissions
                                        </button>
                                        <button
                                            className={`${styles.button} ${styles.buttonIcon} ${styles.deleteButton}`}
                                            onClick={() => handleDelete(assignment._id)}
                                            title="Delete Assignment"
                                        >
                                            <FaTrashAlt />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
    
            {/* --- Modals --- */}
            {showModal && (
                <ClassroomAssignmentsModal onClose={() => setShowModal(false)} assignment={editingAssignment} />
            )}
            {showFilterModal && (
                <ClassroomAssignmentsFilter
                    filter={filter}
                    onApply={applyFilter}
                    onClose={closeFilterModal}
                />
            )}
        </div>
    );
};

export default ClassroomAssignments;